import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Popconfirm,
  message,
  Tag,
  Switch,
  Row,
  Col,
  Spin,
  Empty,
  Radio
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  ArrowLeftOutlined,
  MedicineBoxOutlined,
  ScheduleOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API from '../config/api';
import { ClinicService, ServiceConsumable } from './type';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import { useInventoryContext } from './InventoryContext';
import { useClinicContext } from './ClinicContext';
import {
  findVisitTypeWithSameName,
  isActiveVisitTypeDuplicate,
  selectablePackageServices,
  selectableVisitTypes,
  setVisitTypeActive,
  visibleAdditionalServices,
  visibleCustomVisitTypes,
} from '../utils/serviceCatalogSafety';

interface CustomVisitType {
  type_id: string;
  name: string;
  name_ar: string;
  normal_price: number;
  urgent_price: number;
  is_active: boolean;
  order: number;
}

const STANDARD_TYPE_IDS = ['visit', 'revisit', 'consultation', 'other'];

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const SERVICE_CATEGORIES = [
  { value: 'imaging', label: 'Imaging / أشعة', labelAr: 'أشعة' },
  { value: 'lab', label: 'Laboratory / معمل', labelAr: 'معمل' },
  { value: 'procedure', label: 'Procedure / إجراء', labelAr: 'إجراء' },
  { value: 'examination', label: 'Examination / فحص', labelAr: 'فحص' },
  { value: 'therapy', label: 'Therapy / علاج', labelAr: 'علاج' },
  { value: 'general', label: 'General / عام', labelAr: 'عام' }
];

const COPY_MODE_SEPARATE = 'separate';
const COPY_MODE_COPY = 'copy';

const ServicesManagement: React.FC = () => {
  const [services, setServices] = useState<ClinicService[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingService, setEditingService] = useState<ClinicService | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Custom visit types state
  const [customVisitTypes, setCustomVisitTypes] = useState<CustomVisitType[]>([]);
  const [allVisitTypes, setAllVisitTypes] = useState<CustomVisitType[]>([]);
  const [visitTypesLoading, setVisitTypesLoading] = useState(false);
  const [visitTypeModalVisible, setVisitTypeModalVisible] = useState(false);
  const [editingVisitType, setEditingVisitType] = useState<CustomVisitType | null>(null);
  const [visitTypeForm] = Form.useForm();

  const doctorId = localStorage.getItem('doctorId');
  const { clinics, selectedClinicId, selectedClinic, loading: clinicsLoading } = useClinicContext();
  const { items: inventoryItems } = useInventoryContext();
  const [serviceConsumables, setServiceConsumables] = useState<ServiceConsumable[]>([]);

  const activeClinicId = selectedClinicId && selectedClinicId !== '__all_clinics__'
    ? selectedClinicId
    : clinics[0]?._id || '';
  const hasMultipleClinics = clinics.length > 1;
  const clinicOptions = clinics.map(clinic => ({
    value: clinic._id,
    label: clinic.name || clinic._id
  }));

  useEffect(() => {
    fetchServices();
    fetchCustomVisitTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, activeClinicId]);

  const getClinicLabel = (clinicId?: string) => (
    clinics.find(clinic => clinic._id === clinicId)?.name || clinicId || 'Selected clinic'
  );

  const fetchServices = async () => {
    if (!doctorId) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SERVICES(doctorId)}`, {
        params: {
          includeInactive: 'false',
          ...(activeClinicId ? { clinic_id: activeClinicId } : {})
        }
      });

      if (response.data.success) {
        setServices(visibleAdditionalServices(response.data.data || []));
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      message.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  // Fetch custom visit types from the VisitTypeConfiguration API
  const fetchCustomVisitTypes = async () => {
    if (!doctorId) return;
    try {
      setVisitTypesLoading(true);
      const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`, {
        params: activeClinicId ? { clinic_id: activeClinicId } : {}
      });
      if (response.data.success) {
        const types: CustomVisitType[] = response.data.data.visit_types || [];
        setAllVisitTypes(types);
        setCustomVisitTypes(visibleCustomVisitTypes(
          types.filter(t => !STANDARD_TYPE_IDS.includes(t.type_id))
        ));
      }
    } catch (error) {
      console.error('Error fetching custom visit types:', error);
    } finally {
      setVisitTypesLoading(false);
    }
  };

  const handleAddCustomVisitType = () => {
    setEditingVisitType(null);
    visitTypeForm.resetFields();
    visitTypeForm.setFieldsValue({
      target_clinic_id: activeClinicId,
      data_mode: COPY_MODE_SEPARATE
    });
    setVisitTypeModalVisible(true);
  };

  const handleEditCustomVisitType = (vt: CustomVisitType) => {
    setEditingVisitType(vt);
    visitTypeForm.setFieldsValue({
      name: vt.name,
      name_ar: vt.name_ar,
      price: vt.normal_price,
      target_clinic_id: activeClinicId,
      data_mode: COPY_MODE_SEPARATE
    });
    setVisitTypeModalVisible(true);
  };

  const handleSetCustomVisitTypeActive = async (typeId: string, isActive: boolean) => {
    if (!doctorId) return;
    try {
      const updatedTypes = setVisitTypeActive(allVisitTypes, typeId, isActive);
      await axios.post(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`, {
        visit_types: updatedTypes,
        default_type: 'visit',
        ...(activeClinicId ? { clinic_id: activeClinicId } : {})
      });
      message.success(isActive
        ? 'Visit type restored and available for new visits'
        : 'Visit type removed from the list; old receipts are unchanged');
      fetchCustomVisitTypes();
    } catch (error) {
      console.error('Error changing custom visit type status:', error);
      message.error('Failed to change visit type status');
    }
  };

  const handleSubmitCustomVisitType = async (values: {
    name: string;
    name_ar?: string;
    price: number;
    target_clinic_id?: string;
    data_mode?: string;
    source_clinic_id?: string;
  }) => {
    if (!doctorId) return;
    try {
      const targetClinicId = values.target_clinic_id || activeClinicId;
      const sourceClinicId = values.source_clinic_id;
      let baseVisitTypes = allVisitTypes;

      if (!editingVisitType && values.data_mode === COPY_MODE_COPY && sourceClinicId) {
        const sourceResponse = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`, {
          params: { clinic_id: sourceClinicId }
        });
        if (sourceResponse.data.success) {
          baseVisitTypes = selectableVisitTypes(sourceResponse.data.data.visit_types || []);
        }
      }

      let updatedTypes: CustomVisitType[];
      const duplicate = findVisitTypeWithSameName(
        baseVisitTypes,
        values.name,
        values.name_ar,
        editingVisitType?.type_id
      );

      if (editingVisitType) {
        if (duplicate) {
          message.warning('Another visit type already uses this English or Arabic name');
          return;
        }
        // Update existing visit type in-place, preserving type_id and order
        updatedTypes = baseVisitTypes.map(t =>
          t.type_id === editingVisitType.type_id
            ? {
                ...t,
                name: values.name.trim(),
                name_ar: values.name_ar?.trim() || values.name.trim(),
                normal_price: values.price,
                urgent_price: values.price
              }
            : t
        );
      } else {
        if (isActiveVisitTypeDuplicate(duplicate)) {
          message.warning('This visit type already exists');
          return;
        }

        if (duplicate) {
          // Reuse the historical identifier instead of creating a second row.
          // Old visits keep resolving through the same type_id.
          updatedTypes = baseVisitTypes.map(t =>
            t.type_id === duplicate.type_id
              ? {
                  ...t,
                  name: values.name.trim(),
                  name_ar: values.name_ar?.trim() || values.name.trim(),
                  normal_price: values.price,
                  urgent_price: values.price,
                  is_active: true
                }
              : t
          );
        } else {
          const newType: CustomVisitType = {
            type_id: `custom_${Date.now()}`,
            name: values.name.trim(),
            name_ar: values.name_ar?.trim() || values.name.trim(),
            normal_price: values.price,
            urgent_price: values.price,
            is_active: true,
            order: baseVisitTypes.length + 1
          };
          updatedTypes = [...baseVisitTypes, newType];
        }
      }

      await axios.post(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`, {
        visit_types: updatedTypes,
        default_type: 'visit',
        ...(targetClinicId ? { clinic_id: targetClinicId } : {})
      });
      message.success(editingVisitType
        ? 'Visit type updated successfully'
        : duplicate
          ? 'Hidden visit type restored without creating a duplicate'
          : 'Visit type added successfully');
      setVisitTypeModalVisible(false);
      setEditingVisitType(null);
      fetchCustomVisitTypes();
    } catch (error) {
      console.error('Error saving custom visit type:', error);
      message.error('Failed to save visit type');
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    form.resetFields();
    form.setFieldsValue({
      category: 'general',
      isActive: true,
      target_clinic_id: activeClinicId,
      data_mode: COPY_MODE_SEPARATE
    });
    setServiceConsumables([]);
    setModalVisible(true);
  };

  const handleEditService = (service: ClinicService) => {
    setEditingService(service);
    form.setFieldsValue({
      name: service.name,
      description: service.description,
      price: service.price,
      category: service.category || 'general',
      isActive: service.isActive !== false,
      target_clinic_id: activeClinicId,
      data_mode: COPY_MODE_SEPARATE
    });
    setServiceConsumables(service.consumables ? [...service.consumables] : []);
    setModalVisible(true);
  };

  const handleRemoveService = async (service: ClinicService) => {
    if (!doctorId) return;

    try {
      const response = await axios.delete(
        `${API.BASE_URL}${API.ENDPOINTS.SERVICE(doctorId, service.service_id)}`
      );

      if (response.data.success) {
        setServices(current => current.filter(item => item.service_id !== service.service_id));
        message.success('Service removed; old receipts and financial history are unchanged');
      }
    } catch (error) {
      console.error('Error changing service status:', error);
      message.error('Failed to change service status');
    }
  };

  const handleSubmit = async (values: any) => {
    if (!doctorId) {
      message.error('Doctor ID not found. Please log in again.');
      return;
    }

    try {
      const { target_clinic_id, data_mode, source_clinic_id, ...serviceValues } = values;
      const targetClinicId = target_clinic_id || activeClinicId;
      const payload = {
        ...serviceValues,
        consumables: serviceConsumables,
        ...(targetClinicId ? { clinic_id: targetClinicId } : {})
      };
      if (editingService) {
        const response = await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.SERVICE(doctorId, editingService.service_id)}`,
          payload
        );
        if (response.data.success) {
          message.success('Service updated successfully');
          setModalVisible(false);
          fetchServices();
        }
      } else {
        if (data_mode === COPY_MODE_COPY && source_clinic_id) {
          const sourceResponse = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SERVICES(doctorId)}`, {
            params: {
              includeInactive: 'false',
              clinic_id: source_clinic_id
            }
          });
          const sourceServices = sourceResponse.data.success
            ? selectablePackageServices<ClinicService>((sourceResponse.data.data || []) as ClinicService[])
            : [];
          const copiedServices = sourceServices.map((service: ClinicService) => ({
            name: service.name,
            description: service.description || '',
            price: service.price,
            category: service.category || 'general',
            isActive: service.isActive !== false,
            consumables: service.consumables || [],
            doctor_id: doctorId,
            ...(targetClinicId ? { clinic_id: targetClinicId } : {})
          }));
          if (copiedServices.length > 0) {
            await axios.post(`${API.BASE_URL}${API.ENDPOINTS.SERVICES_BATCH_ASSIGN}`, {
              services: copiedServices
            });
          }
        }

        const response = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.SERVICES}`, {
          ...payload,
          doctor_id: doctorId
        });
        if (response.data.success) {
          message.success('Service created successfully');
          setModalVisible(false);
          fetchServices();
        }
      }
    } catch (error: any) {
      console.error('Error saving service:', error);
      message.error(error?.response?.data?.message || 'Failed to save service');
    }
  };

  const handleAddConsumable = () => {
    setServiceConsumables(prev => [
      ...prev,
      { inventory_id: '', inventory_name: '', quantity_per_use: 1, unit: '' }
    ]);
  };

  const handleRemoveConsumable = (index: number) => {
    setServiceConsumables(prev => prev.filter((_, i) => i !== index));
  };

  const handleConsumableChange = (index: number, field: keyof ServiceConsumable, value: any) => {
    setServiceConsumables(prev => {
      const updated = [...prev];
      if (field === 'inventory_id') {
        const inv = inventoryItems.find((i: any) => i._id === value);
        updated[index] = {
          ...updated[index],
          inventory_id: value,
          inventory_name: inv?.name || '',
          unit: inv?.unit || updated[index].unit
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      imaging: 'blue',
      lab: 'purple',
      procedure: 'orange',
      examination: 'green',
      therapy: 'cyan',
      general: 'default'
    };
    return colors[category] || 'default';
  };

  const columns = [
    {
      title: 'Service Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => {
        const cat = SERVICE_CATEGORIES.find(c => c.value === category);
        return <Tag color={getCategoryColor(category)}>{cat?.labelAr || category}</Tag>;
      }
    },
    {
      title: 'Price (EGP)',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => (
        <Text strong style={{ color: '#52c41a' }}>{price.toLocaleString()} EGP</Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive !== false ? 'green' : 'red'}>
          {isActive !== false ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Consumables',
      key: 'consumables',
      render: (_: any, record: ClinicService) => (
        record.consumables && record.consumables.length > 0
          ? <Tag color="volcano" icon={<ExperimentOutlined />}>{record.consumables.length} items</Tag>
          : <Tag color="default">None</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ClinicService) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditService(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Remove this service from Additional Services?"
            description="It will disappear from new selections. Existing receipts and financial history will not change."
            onConfirm={() => handleRemoveService(record)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button type="link" danger icon={<EyeInvisibleOutlined />}>
              Remove
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <Layout>
        <DashboardHeader
          onSettingsClick={() => navigate('/settings')}
          onMenuClick={() => { }}
          isMobile={false}
        />

        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
            <Col>
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </Button>
                <Title level={3} style={{ margin: 0 }}>
                  <MedicineBoxOutlined style={{ marginRight: 8 }} />
                  Services Management
                </Title>
              </Space>
            </Col>
            {hasMultipleClinics && (
              <Col>
                <Text type="secondary">
                  Current branch: <Text strong>{selectedClinic?.name || getClinicLabel(activeClinicId)}</Text>
                </Text>
              </Col>
            )}
          </Row>

          {/* Custom Visit Types Card */}
          <Card
            style={{ marginBottom: 24 }}
            title={
              <Space>
                <ScheduleOutlined style={{ color: '#722ed1' }} />
                <span>Custom Visit Types / أنواع الكشف المخصصة</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddCustomVisitType}
                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
              >
                Add Visit Type
              </Button>
            }
          >
            <Spin spinning={visitTypesLoading}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Removed visit types disappear from this list and new visits. Old visits, receipts, and reports stay unchanged. Adding the same name again safely restores the original item.
              </Text>
              {customVisitTypes.length === 0 ? (
                <Empty
                  description="No custom visit types yet. Click 'Add Visit Type' to create one."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Table
                  dataSource={customVisitTypes}
                  rowKey="type_id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      key: 'name',
                      render: (text: string) => <Text strong>{text}</Text>
                    },
                    {
                      title: 'Arabic Name / الاسم بالعربي',
                      dataIndex: 'name_ar',
                      key: 'name_ar'
                    },
                    {
                      title: 'Price (EGP)',
                      dataIndex: 'normal_price',
                      key: 'normal_price',
                      render: (price: number) => (
                        <Text strong style={{ color: '#52c41a' }}>{price.toLocaleString()} EGP</Text>
                      )
                    },
                    {
                      title: 'Status',
                      dataIndex: 'is_active',
                      key: 'is_active',
                      render: (isActive: boolean) => (
                        <Tag color={isActive !== false ? 'green' : 'default'}>
                          {isActive !== false ? 'Active' : 'Hidden'}
                        </Tag>
                      )
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_: any, record: CustomVisitType) => (
                        <Space>
                          <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEditCustomVisitType(record)}
                            style={{ color: '#722ed1' }}
                          >
                            Edit
                          </Button>
                          <Popconfirm
                            title="Remove this visit type from the list?"
                            description="It will disappear from new visits. Old visits and receipts will keep their original label and price."
                            onConfirm={() => handleSetCustomVisitTypeActive(record.type_id, false)}
                            okText="Remove"
                            cancelText="Cancel"
                          >
                            <Button type="link" danger icon={<EyeInvisibleOutlined />}>
                              Remove
                            </Button>
                          </Popconfirm>
                        </Space>
                      )
                    }
                  ]}
                />
              )}
            </Spin>
          </Card>

          {/* Add / Edit Custom Visit Type Modal */}
          <Modal
            title={editingVisitType ? 'Edit Visit Type / تعديل نوع الكشف' : 'Add Custom Visit Type / إضافة نوع كشف'}
            open={visitTypeModalVisible}
            onCancel={() => { setVisitTypeModalVisible(false); setEditingVisitType(null); }}
            footer={null}
            width={480}
          >
            <Form form={visitTypeForm} layout="vertical" onFinish={handleSubmitCustomVisitType}>
              {hasMultipleClinics && (
                <>
                  <Form.Item
                    name="target_clinic_id"
                    label="Branch / الفرع"
                    rules={[{ required: true, message: 'Please select the branch' }]}
                  >
                    <Select
                      loading={clinicsLoading}
                      options={clinicOptions}
                      placeholder="Select branch"
                    />
                  </Form.Item>
                  {!editingVisitType && (
                    <>
                      <Form.Item
                        name="data_mode"
                        label="Data setup / طريقة الإضافة"
                      >
                        <Radio.Group>
                          <Radio.Button value={COPY_MODE_SEPARATE}>Set separately</Radio.Button>
                          <Radio.Button value={COPY_MODE_COPY}>Copy from another clinic</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.data_mode !== cur.data_mode || prev.target_clinic_id !== cur.target_clinic_id}>
                        {({ getFieldValue }) => getFieldValue('data_mode') === COPY_MODE_COPY ? (
                          <Form.Item
                            name="source_clinic_id"
                            label="Copy existing data from / نسخ البيانات من"
                            rules={[{ required: true, message: 'Please select source clinic' }]}
                          >
                            <Select
                              loading={clinicsLoading}
                              options={clinicOptions.filter(option => option.value !== getFieldValue('target_clinic_id'))}
                              placeholder="Select source clinic"
                            />
                          </Form.Item>
                        ) : null}
                      </Form.Item>
                    </>
                  )}
                </>
              )}
              <Form.Item
                name="name"
                label="Visit Type Name (English)"
                rules={[{ required: true, message: 'Please enter the visit type name' }]}
              >
                <Input placeholder="e.g. Follow-up, Procedure" />
              </Form.Item>
              <Form.Item
                name="name_ar"
                label="Visit Type Name (Arabic / اسم النوع بالعربي)"
              >
                <Input placeholder="مثال: متابعة، إجراء" dir="rtl" />
              </Form.Item>
              <Form.Item
                name="price"
                label="Price (EGP)"
                rules={[
                  { required: true, message: 'Please enter the price' },
                  { type: 'number', min: 0, message: 'Price cannot be negative' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0"
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => Number(value!.replace(/\$\s?|(,*)/g, '')) as any}
                  addonAfter="EGP"
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => { setVisitTypeModalVisible(false); setEditingVisitType(null); }}>Cancel</Button>
                  <Button type="primary" htmlType="submit" style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}>
                    {editingVisitType ? 'Save Changes' : 'Add Visit Type'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          {/* Additional Services Card */}
          <Card
            title={
              <Space>
                <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                <span>Additional Services / خدمات إضافية</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddService}
              >
                Add New Service
              </Button>
            }
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Removed services disappear from this section and new selections. Existing receipts, package history, and revenue records stay unchanged.
            </Text>
            <Table
              columns={columns}
              dataSource={visibleAdditionalServices(services)}
              rowKey="service_id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>

          {/* Add/Edit Service Modal */}
          <Modal
            title={editingService ? 'Edit Service' : 'Add New Service'}
            open={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={null}
            width={680}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              {hasMultipleClinics && (
                <>
                  <Form.Item
                    name="target_clinic_id"
                    label="Branch / الفرع"
                    rules={[{ required: true, message: 'Please select the branch' }]}
                  >
                    <Select
                      loading={clinicsLoading}
                      options={clinicOptions}
                      placeholder="Select branch"
                    />
                  </Form.Item>
                  {!editingService && (
                    <>
                      <Form.Item
                        name="data_mode"
                        label="Data setup / طريقة الإضافة"
                      >
                        <Radio.Group>
                          <Radio.Button value={COPY_MODE_SEPARATE}>Set separately</Radio.Button>
                          <Radio.Button value={COPY_MODE_COPY}>Copy from another clinic</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.data_mode !== cur.data_mode || prev.target_clinic_id !== cur.target_clinic_id}>
                        {({ getFieldValue }) => getFieldValue('data_mode') === COPY_MODE_COPY ? (
                          <Form.Item
                            name="source_clinic_id"
                            label="Copy existing data from / نسخ البيانات من"
                            rules={[{ required: true, message: 'Please select source clinic' }]}
                          >
                            <Select
                              loading={clinicsLoading}
                              options={clinicOptions.filter(option => option.value !== getFieldValue('target_clinic_id'))}
                              placeholder="Select source clinic"
                            />
                          </Form.Item>
                        ) : null}
                      </Form.Item>
                    </>
                  )}
                </>
              )}
              <Form.Item
                name="name"
                label="Service Name"
                rules={[{ required: true, message: 'Please enter service name' }]}
              >
                <Input placeholder="e.g., Ultrasound / سونار" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
              >
                <Input.TextArea rows={2} placeholder="Brief description of the service" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="price"
                    label="Price (EGP)"
                    rules={[
                      { required: true, message: 'Please enter price' },
                      { type: 'number', min: 0, message: 'Price cannot be negative' }
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0"
                      min={0}
                      formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => Number(value!.replace(/\$\s?|(,*)/g, '')) as any}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="category"
                    label="Category"
                    rules={[{ required: true, message: 'Please select a category' }]}
                  >
                    <Select placeholder="Select category">
                      {SERVICE_CATEGORIES.map(cat => (
                        <Option key={cat.value} value={cat.value}>
                          {cat.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="isActive"
                label="Active Status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>

              {/* Consumables Section */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong style={{ color: '#d46b08' }}>
                    <ExperimentOutlined style={{ marginRight: 6 }} />
                    Inventory Consumables / المستهلكات
                  </Text>
                  <Button
                    size="small"
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddConsumable}
                    disabled={inventoryItems.length === 0}
                  >
                    Add Item
                  </Button>
                </div>
                {inventoryItems.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    No inventory items found. Add items in Inventory Management first.
                  </Text>
                )}
                {serviceConsumables.map((consumable, index) => (
                  <Row key={index} gutter={8} style={{ marginBottom: 6, alignItems: 'center' }}>
                    <Col span={11}>
                      <Select
                        size="small"
                        placeholder="Select inventory item"
                        style={{ width: '100%' }}
                        value={consumable.inventory_id || undefined}
                        onChange={(val) => handleConsumableChange(index, 'inventory_id', val)}
                        showSearch
                        optionFilterProp="children"
                      >
                        {inventoryItems.map((inv: any) => (
                          <Option key={inv._id} value={inv._id}>
                            {inv.name} ({inv.quantity} {inv.unit})
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={6}>
                      <InputNumber
                        size="small"
                        min={0.01}
                        step={0.5}
                        placeholder="Qty"
                        style={{ width: '100%' }}
                        value={consumable.quantity_per_use}
                        onChange={(val) => handleConsumableChange(index, 'quantity_per_use', val || 1)}
                      />
                    </Col>
                    <Col span={4}>
                      <Text style={{ fontSize: 12, color: '#888' }}>{consumable.unit || '-'}</Text>
                    </Col>
                    <Col span={3}>
                      <Button
                        size="small"
                        type="text"
                        danger
                        onClick={() => handleRemoveConsumable(index)}
                      >
                        ✕
                      </Button>
                    </Col>
                  </Row>
                ))}
                {serviceConsumables.length > 0 && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    These items will be automatically deducted from inventory when this service is billed.
                  </Text>
                )}
              </div>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setModalVisible(false)}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit">
                    {editingService ? 'Update Service' : 'Add Service'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>
        </Content>
      </Layout>
    </Layout >
  );
};

export default ServicesManagement;

